import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReservationService } from '@app/reservation/reservation.service';
import { TranslateService } from '@ngx-translate/core';

export interface MealFieldConfig {
  key: string;
  label: string;
  type: 'toggle' | 'time-select' | 'number' | 'placement-mode' | 'template-select' | 'chip';
  defaultValue: any;
  validators?: any[];
  mealTypes?: string[];
}

export const MEAL_FIELD_CONFIGS: MealFieldConfig[] = [
  { key: 'enabled', label: 'PMSSettings.Enabled', type: 'toggle', defaultValue: false },
  {
    key: 'duration',
    label: 'PMSSettings.Duration',
    type: 'number',
    defaultValue: '60',
    validators: [Validators.min(1), Validators.max(480)]
  },
  { key: 'time', label: 'PMSSettings.Time', type: 'time-select', defaultValue: '08:00' },
  { key: 'placementMode', label: 'PMSSettings.PlacementMode', type: 'placement-mode', defaultValue: 'import_only' },
  { key: 'templateId', label: 'PMSSettings.Template', type: 'template-select', defaultValue: null },
  {
    key: 'servedOnDeparture',
    label: 'PMSSettings.ServedOnDeparture',
    type: 'toggle',
    defaultValue: true,
    mealTypes: ['breakfast']
  },
  {
    key: 'servedOnArrival',
    label: 'PMSSettings.ServedOnArrival',
    type: 'toggle',
    defaultValue: true,
    mealTypes: ['dinner']
  },
  { key: 'arrivalDay', label: 'PMSSettings.ArrivalDay', type: 'chip', defaultValue: false, mealTypes: ['lunch'] },
  { key: 'departureDay', label: 'PMSSettings.DepartureDay', type: 'chip', defaultValue: false, mealTypes: ['lunch'] },
  { key: 'automaticPlacement', label: 'PMSSettings.AutomaticPlacement', type: 'toggle', defaultValue: false }
];

export const GLOBAL_FIELD_CONFIGS: MealFieldConfig[] = [
  // { key: 'dateFilter', label: 'PMSSettings.DateFilter', type: 'template-select', defaultValue: 'arrival' },
  {
    key: 'leadTime',
    label: 'PMSSettings.LeadTime',
    type: 'number',
    defaultValue: '1',
    validators: [Validators.min(1), Validators.max(365)]
  },
  {
    key: 'sameTableAllMeals',
    label: 'PMSSettings.SameTableAllMeals',
    type: 'toggle',
    defaultValue: false
  }
];

export type MealType = 'breakfast' | 'lunch' | 'dinner';

export const MEALS: {
  type: MealType;
  icon: string;
  labelKey: string;
  defaultEnabled: boolean;
  defaultDuration: string;
  defaultTime: string;
}[] = [
  {
    type: 'breakfast',
    icon: 'free_breakfast',
    labelKey: 'PMSSettings.Breakfast',
    defaultEnabled: false,
    defaultDuration: '60',
    defaultTime: '08:00'
  },
  {
    type: 'lunch',
    icon: 'wb_sunny',
    labelKey: 'PMSSettings.Lunch',
    defaultEnabled: false,
    defaultDuration: '60',
    defaultTime: '13:00'
  },
  {
    type: 'dinner',
    icon: 'brightness_3',
    labelKey: 'PMSSettings.Dinner',
    defaultEnabled: false,
    defaultDuration: '90',
    defaultTime: '19:00'
  }
];

@Component({
  selector: 'app-pms-settings',
  templateUrl: './pms-settings.component.html',
  styleUrls: ['./pms-settings.component.scss']
})
export class PMSSettingsComponent implements OnInit, OnDestroy {
  @Input() clientId: number;
  @Input() shifts: any[] = [];

  form: FormGroup;
  saving = false;
  loading = false;

  meals = MEALS;
  mealFieldConfigs = MEAL_FIELD_CONFIGS;
  globalFieldConfigs = GLOBAL_FIELD_CONFIGS;

  // dateFilterOptions = [
  //   { value: 'arrival', label: 'PMSSettings.DateArrival' },
  //   { value: 'stay', label: 'PMSSettings.DateStay' }
  // ];

  timeOptions: { label: string; value: string }[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private reservationService: ReservationService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this._buildTimeOptions();
    this._buildForm();
    this._loadSettings();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // -------------------------------------------------------------------------
  // Form construction
  // -------------------------------------------------------------------------

  private _buildForm() {
    const mealGroups: any = {};
    for (const meal of MEALS) {
      const group: any = {};
      for (const field of MEAL_FIELD_CONFIGS) {
        if (field.mealTypes && !field.mealTypes.includes(meal.type)) continue;
        let defaultVal = field.defaultValue;
        if (field.key === 'enabled') defaultVal = meal.defaultEnabled;
        if (field.key === 'duration') defaultVal = meal.defaultDuration;
        if (field.key === 'time') defaultVal = meal.defaultTime;
        group[field.key] = [defaultVal, field.validators || []];
      }
      mealGroups[meal.type] = this.fb.group(group);
    }

    const globalGroup: any = {};
    for (const field of GLOBAL_FIELD_CONFIGS) {
      globalGroup[field.key] = [field.defaultValue, field.validators || []];
    }

    this.form = this.fb.group({
      ...mealGroups,
      __global: this.fb.group(globalGroup)
    });
  }

  // -------------------------------------------------------------------------
  // Data loading — server stores '0'/'1' strings, form uses booleans for toggles
  // -------------------------------------------------------------------------

  private _loadSettings() {
    this.loading = true;
    this.reservationService
      .getPMSSettings(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp: any) => {
          this.loading = false;
          if (resp && resp.success && resp.data) {
            this._patchForm(resp.data);
          }
        },
        () => {
          this.loading = false;
          this._showError('PMSSettings.LoadError');
        }
      );
  }
  private _patchForm(data: Record<string, Record<string, string>>) {
    const BOOL_KEYS = new Set([
      'enabled',
      'automaticPlacement',
      'servedOnDeparture',
      'servedOnArrival',
      'arrivalDay',
      'departureDay',
      'sameTableAllMeals'
    ]);
    const NUMBER_KEYS = new Set(['templateId', 'duration', 'leadTime']);

    for (const mealType of [...MEALS.map(m => m.type), '__global']) {
      const group = this.form.get(mealType) as FormGroup;
      if (!group || !data[mealType]) continue;

      const converted: any = {};
      for (const key of Object.keys(data[mealType])) {
        const val = data[mealType][key];
        if (BOOL_KEYS.has(key)) {
          converted[key] = val === '1';
        } else if (NUMBER_KEYS.has(key)) {
          converted[key] = val == null ? null : Number(val);
        } else {
          converted[key] = val;
        }
      }
      group.patchValue(converted, { emitEvent: false });
    }
  }

  // -------------------------------------------------------------------------
  // Saving — convert booleans back to '0'/'1' strings for the server
  // -------------------------------------------------------------------------

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.reservationService
      .putPMSSettings(this.clientId, this._buildPayload())
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.saving = false;
          this.snackBar.open(this.translate.instant('PMSSettings.Saved'), '', {
            duration: 2000,
            panelClass: ['snackbar-success']
          });
        },
        () => {
          this.saving = false;
          this._showError('PMSSettings.SaveError');
        }
      );
  }

  private _buildPayload(): Record<string, Record<string, string>> {
    const BOOL_KEYS = new Set([
      'enabled',
      'automaticPlacement',
      'servedOnDeparture',
      'servedOnArrival',
      'arrivalDay',
      'departureDay',
      'sameTableAllMeals'
    ]);

    const raw = this.form.getRawValue();
    const payload: any = {};
    for (const mealType of Object.keys(raw)) {
      payload[mealType] = {};
      for (const key of Object.keys(raw[mealType])) {
        const val = raw[mealType][key];
        if (BOOL_KEYS.has(key)) {
          payload[mealType][key] = val ? '1' : '0';
        } else {
          payload[mealType][key] = val == null ? null : String(val);
        }
      }
    }
    return payload;
  }

  // -------------------------------------------------------------------------
  // Template helpers
  // -------------------------------------------------------------------------

  mealGroup(type: string): FormGroup {
    return this.form.get(type) as FormGroup;
  }

  globalGroup(): FormGroup {
    return this.form.get('__global') as FormGroup;
  }

  isEnabled(mealType: string): boolean {
    const g = this.mealGroup(mealType);
    return g && g.get('enabled') && g.get('enabled').value === true;
  }

  isAutomaticPlacementOn(mealType: string): boolean {
    const g = this.mealGroup(mealType);
    return g && g.get('automaticPlacement') && g.get('automaticPlacement').value === true;
  }

  isUseTemplate(mealType: string): boolean {
    const g = this.mealGroup(mealType);
    return g && g.get('placementMode') && g.get('placementMode').value === 'use_template';
  }

  setPlacementMode(mealType: string, mode: 'import_only' | 'use_template') {
    const g = this.mealGroup(mealType);
    if (!g) return;
    g.get('placementMode').setValue(mode);
    if (mode === 'import_only') {
      g.get('templateId').setValue(null);
    }
  }

  toggleChip(mealType: string, key: string) {
    const g = this.mealGroup(mealType);
    if (!g || !g.get(key)) return;
    g.get(key).setValue(!g.get(key).value);
  }

  isChipActive(mealType: string, key: string): boolean {
    const g = this.mealGroup(mealType);
    return g && g.get(key) && g.get(key).value === true;
  }

  fieldsForMeal(mealType: string): MealFieldConfig[] {
    return MEAL_FIELD_CONFIGS.filter(f => !f.mealTypes || f.mealTypes.includes(mealType));
  }

  private _buildTimeOptions() {
    for (let h = 0; h < 24; h++) {
      for (const m of [0, 30]) {
        const hh = h.toString().padStart(2, '0');
        const mm = m.toString().padStart(2, '0');
        this.timeOptions.push({ label: `${hh}:${mm}`, value: `${hh}:${mm}` });
      }
    }
  }

  private _showError(key: string) {
    this.snackBar.open(this.translate.instant(key), 'Close', { duration: 3000, panelClass: ['snackbar-error'] });
  }
}
